import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Phone, Mail, MapPin, Car, ShieldAlert, History, ChevronRight } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: "flagged", label: "Flagged", color: "text-[#F2B705] border-[#F2B705]/40 bg-[#F2B705]/10" },
  { key: "contacted", label: "Contacted", color: "text-blue-300 border-blue-400/40 bg-blue-400/10" },
  { key: "final_notice", label: "Final Notice", color: "text-orange-300 border-orange-400/40 bg-orange-400/10" },
  { key: "recovery", label: "In Recovery", color: "text-red-300 border-red-400/40 bg-red-400/10" },
  { key: "repossessed", label: "Repossessed", color: "text-red-500 border-red-600/50 bg-red-600/15" },
  { key: "resolved", label: "Resolved", color: "text-green-400 border-green-500/40 bg-green-500/10" },
];
const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.key, s]));

function StageBadge({ stage }) {
  const s = STAGE_MAP[stage] || STAGES[0];
  return <span data-testid={`recovery-badge-${stage}`} className={`text-xs px-2.5 py-1 rounded-full border ${s.color}`}>{s.label}</span>;
}

function StatBox({ label, value, tone = "white" }) {
  const tones = { white: "text-white", danger: "text-red-400", warn: "text-[#F2B705]", ok: "text-green-400" };
  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="text-white/45 text-xs uppercase tracking-widest">{label}</div>
      <div className={`font-display font-black text-3xl mt-1.5 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function RecoveryCard({ item, token, onUpdated }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const update = async (stage) => {
    setBusy(true);
    try {
      await axios.post(`${API}/agreements/${item.agreement_id}/recovery`, { stage, note: note.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Updated to "${STAGE_MAP[stage]?.label || stage}".`);
      setNote("");
      onUpdated();
    } catch {
      toast.error("Could not update recovery status.");
    } finally {
      setBusy(false);
    }
  };

  const toggleEscalation = async () => {
    if (!item.booking_id) { toast.error("No linked booking for this case."); return; }
    setBusy(true);
    const action = item.escalation_stopped ? "resume-escalation" : "stop-escalation";
    try {
      await axios.post(`${API}/bookings/${item.booking_id}/${action}`, { reason: note.trim() || "Renter squared up" }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(item.escalation_stopped ? "Auto-texts resumed." : "Auto-texts stopped for this rental.");
      setNote("");
      onUpdated();
    } catch {
      toast.error("Could not update escalation.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`recovery-card-${item.agreement_id}`} className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-semibold">
            <span className="truncate">{item.renter_name}</span>
            <StageBadge stage={item.stage} />
          </div>
          <div className="text-white/45 text-xs mt-1 flex items-center gap-1.5"><Car size={12} /> {item.vehicle} {item.license_plate ? `· ${item.license_plate}` : ""}</div>
        </div>
        {item.days_overdue != null && item.days_overdue > 0 && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 font-semibold whitespace-nowrap">
            {item.days_overdue}d overdue
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-white/55">
        <span>Due back: <b className="text-white/80">{item.return_date || "—"}</b></span>
        {item.balance_due && <span>Balance: <b className="text-white/80">{item.balance_due}</b></span>}
        {item.renter_phone && <a href={`tel:${item.renter_phone}`} className="flex items-center gap-1 text-[#F2B705] hover:underline"><Phone size={11} /> {item.renter_phone}</a>}
        {item.renter_email && <a href={`mailto:${item.renter_email}`} className="flex items-center gap-1 text-[#F2B705] hover:underline truncate"><Mail size={11} /> {item.renter_email}</a>}
        {item.renter_address && <span className="flex items-center gap-1 sm:col-span-2"><MapPin size={11} /> {item.renter_address}</span>}
      </div>

      <textarea
        data-testid={`recovery-note-input-${item.agreement_id}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Add a note (e.g. 'Left voicemail, no answer')…"
        rows={2}
        className="w-full bg-[#0C0C0C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50"
      />

      <div className="flex flex-wrap gap-2">
        {STAGES.map((s) => (
          <button
            key={s.key}
            data-testid={`recovery-set-${s.key}-${item.agreement_id}`}
            disabled={busy || s.key === item.stage}
            onClick={() => update(s.key)}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${s.color} hover:brightness-125`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1 border-t border-white/5 mt-1">
        <button
          data-testid={`recovery-escalation-toggle-${item.agreement_id}`}
          disabled={busy}
          onClick={toggleEscalation}
          className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors disabled:opacity-40 flex items-center gap-1.5 ${item.escalation_stopped ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-red-300 border-red-500/40 bg-red-500/10 hover:brightness-125"}`}
        >
          {item.escalation_stopped ? "▶ Resume Auto-Texts" : "🔕 Stop Auto-Texts"}
        </button>
        <span className="text-white/40 text-xs">
          {item.escalation_stopped ? "Escalation texts are OFF for this rental." : "Escalation texts are active."}
        </span>
      </div>

      {item.notes && item.notes.length > 0 && (
        <div>
          <button data-testid={`recovery-history-toggle-${item.agreement_id}`} onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80">
            <History size={12} /> Activity log ({item.notes.length}) <ChevronRight size={12} className={`transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
          {open && (
            <div className="mt-2 space-y-1.5 border-l border-white/10 pl-3">
              {item.notes.map((n, i) => (
                <div key={i} className="text-xs">
                  <span className="text-white/75">{n.text}</span>
                  <span className="text-white/35 ml-1.5">{fmtDateTime(n.at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function RecoveryManager({ token, onAuthError }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = () => axios.get(`${API}/recovery`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setData(r.data))
    .catch((e) => { if (e?.response?.status === 401) onAuthError?.(); })
    .finally(() => setLoading(false));

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [token]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  if (!data) return <p className="text-white/40">Could not load recovery data.</p>;

  const s = data.stats;
  return (
    <div data-testid="recovery-dashboard" className="space-y-6">
      <div>
        <h2 className="font-display font-black text-2xl flex items-center gap-2"><ShieldAlert className="text-red-400" size={22} /> Recovery &amp; Repossession</h2>
        <p className="text-white/45 text-sm mt-1">Track overdue rentals and manage escalation from first flag to repossession.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Cases" value={s.total} />
        <StatBox label="Active" value={s.active} tone="warn" />
        <StatBox label="Repossessed" value={s.repossessed} tone="danger" />
        <StatBox label="Resolved" value={s.resolved} tone="ok" />
      </div>

      {data.items.length === 0 ? (
        <div data-testid="recovery-empty" className="text-center py-16 border border-white/10 rounded-xl bg-[#0A0A0A]">
          <AlertTriangle className="mx-auto text-green-400/60 mb-3" size={36} />
          <p className="text-white/60">No overdue rentals. Your whole fleet is on schedule. 🎉</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.items.map((item) => (
            <RecoveryCard key={item.agreement_id} item={item} token={token} onUpdated={fetchData} />
          ))}
        </div>
      )}
    </div>
  );
}
